import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectSidebarCollapsed,
  toggleSidebarCollapse,
  selectSidebarOpen,
  setSidebarOpen,
} from '@store/uiSlice';
import WorkspaceSwitcher from '@components/workspace/WorkspaceSwitcher';
import { useAuth } from '@hooks/useAuth';
import { usePermissions } from '@hooks/usePermissions';
import { useCreateWorkspaceMutation } from '@api/workspaceApi';
import { resolveRoutePermission } from '@config/routePermissions';
import { Avatar } from '@components/ui';
import {
  LayoutDashboard, FolderKanban, CheckSquare, Timer, Columns3, BarChart3,
  Milestone, MessageCircle, Clock, DollarSign, Target, FileBarChart, FileText,
  Workflow, CalendarDays, ShieldCheck, Shield, Users, Settings, PanelLeftClose,
  PanelLeftOpen, ChevronDown, ChevronRight, Plus, Sparkles, Gauge, X, BookOpen,
  Monitor, Globe, Layers, Receipt, Plug, LayoutTemplate,
} from 'lucide-react';

// ── Navigation Configuration — UNCHANGED ──
const NAV_SECTIONS = [
  {
    label: 'Main',
    items: [{ path: '/', icon: LayoutDashboard, label: 'Dashboard', exact: true }],
  },
  {
    label: 'Plan & Execute',
    items: [
      { path: '/projects', icon: FolderKanban, label: 'Projects', badge: null },
      { path: '/tasks', icon: CheckSquare, label: 'Tasks' },
      { path: '/sprints', icon: Timer, label: 'Sprints' },
      { path: '/kanban', icon: Columns3, label: 'Kanban Board' },
      { path: '/gantt', icon: BarChart3, label: 'Gantt Chart' },
      { path: '/milestones', icon: Milestone, label: 'Milestones' },
      { path: '/calendar', icon: CalendarDays, label: 'Calendar' },
      { path: '/portfolio', icon: Layers, label: 'Portfolio' },
    ],
  },
  {
    label: 'Collaborate',
    items: [
      { path: '/documents', icon: FileText, label: 'Documents' },
      { path: '/knowledge-base', icon: BookOpen, label: 'Knowledge Base' },
    ],
  },
  {
    label: 'Track & Report',
    items: [
      { path: '/timesheets', icon: Clock, label: 'Timesheets' },
      { path: '/budget', icon: DollarSign, label: 'Budget & Finance' },
      { path: '/goals', icon: Target, label: 'Goals' },
      { path: '/reports', icon: FileBarChart, label: 'Reports' },
    ],
  },
  {
    label: 'Accounts & Sales',
    items: [
      { path: '/accounts', icon: Receipt, label: 'Accounts' },
      // LEGACY REMOVED — { path: '/invoicing', icon: FileText, label: 'Invoicing (Legacy)' },
      { path: '/approvals', icon: ShieldCheck, label: 'Approvals' },
    ],
  },
  {
    label: 'People',
    items: [
      { path: '/employees', icon: Users, label: 'Employees' },
      { path: '/members', icon: Users, label: 'Team Members' },
    ],
  },
  {
    label: 'Manage',
    items: [
      { path: '/capacity', icon: Gauge, label: 'CapacityIQ' },
      { path: '/workflows', icon: Workflow, label: 'Workflows' },
      { path: '/module-workflows', icon: Settings, label: 'Module Workflows' },
      { path: '/templates', icon: LayoutTemplate, label: 'Templates' },
      { path: '/zuno', icon: Sparkles, label: 'Zuno AI' },
      { path: '/studio', icon: Sparkles, label: 'Zuno Studio' },
      { path: '/agent-space', icon: Sparkles, label: 'Agent Space' },
      { path: '/settings', icon: Settings, label: 'Settings' },
      { path: '/client-manage', icon: Globe, label: 'Client Portal' },
      { path: '/integrations', icon: Plug, label: 'Integrations' },
      { path: '/download', icon: Monitor, label: 'Desktop App' },
    ],
  },
];

// READY_PATHS — UNCHANGED
const READY_PATHS = new Set([
  '/', '/members', '/settings', '/ui', '/projects', '/tasks', '/sprints',
  '/kanban', '/gantt', '/milestones', '/calendar', '/timesheets', '/budget',
  '/capacity', '/goals', '/documents', '/knowledge-base', '/reports',
  '/workflows', '/zuno', '/hivelink', '/download', '/client-manage', '/rbac', '/integrations',
  '/portfolio', '/templates', '/studio', '/agent-space',
  '/employees',
  '/module-workflows',
  '/dependency-graph',
  '/task-approvals',
  '/accounts',
  '/approvals',
]);

// ── NavIcon ──
// active   → white  (sits on dark gradient pill)
// disabled → muted grey
// idle     → #2a2a72 brand indigo (dark end of the theme gradient)
//
// C10 fix (2026-04-20): the local PATH_PERMISSIONS map that used to live here
// has been moved to `client/src/config/routePermissions.js` so the sidebar
// and the route-guard config share a single source of truth. The sidebar
// asks that registry which permission a path needs, and asks usePermissions()
// whether the current user holds it.

const GradientIcon = ({ icon: Icon, size, isActive, isDisabled }) => {
  const color = isActive ? '#ffffff' : isDisabled ? '#b0b0bc' : '#2a2a72';
  return <Icon size={size} color={color} style={{ flexShrink: 0 }} />;
};

const Sidebar = ({ onCreateWorkspace }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const collapsed = useSelector(selectSidebarCollapsed);
  const mobileOpen = useSelector(selectSidebarOpen);
  const { user } = useAuth();
  const { has: hasServerPerm, isLoading: permsLoading, isError: permsError } = usePermissions();

  // Check if the user has permission to see a given path. Asks the registry
  // (C10) which permission(s) the path requires, then asks usePermissions()
  // whether the current user holds them.
  //
  // While permissions are still loading on first render, we optimistically
  // show every entry to avoid a flash of missing nav items. If the server
  // errors out on /rbac/me/permissions, we also fall back to showing the
  // entry — better to show an entry the user can't actually reach than to
  // hide their entire sidebar because of a transient network error (the
  // server-side requirePermission middleware is the source of truth anyway).
  const { hasAny, hasAll } = usePermissions();
  const hasPermission = (path) => {
    const spec = resolveRoutePermission(path);
    if (!spec) return true;
    if (permsLoading || permsError) return true;
    if (typeof spec === 'string') return hasServerPerm(spec);
    if (spec.anyOf) return hasAny(spec.anyOf);
    if (spec.allOf) return hasAll(spec.allOf);
    return true;
  };

  const [expandedSections, setExpandedSections] = useState(
    NAV_SECTIONS.reduce((acc, s) => ({ ...acc, [s.label]: true }), {}),
  );

  // ── All logic UNCHANGED ──
  const toggleSection = (label) => {
    if (collapsed) return;
    setExpandedSections((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const closeMobile = () => dispatch(setSidebarOpen(false));

  const isPathActive = (path, exact) =>
    exact ? location.pathname === path : location.pathname.startsWith(path);

  // navLinkClass — same routing logic, updated visual classes
  const navLinkClass = (path, exact) => {
    const active = isPathActive(path, exact);
    const ready = READY_PATHS.has(path);
    if (!ready) return 'flex items-center gap-3 px-3 py-[7px] text-sm rounded-xl text-surface-300 cursor-not-allowed';
    if (active) return 'flex items-center gap-3 px-3 py-[7px] text-sm font-semibold rounded-xl text-white shadow-md';
    return 'flex items-center gap-3 px-3 py-[7px] text-sm font-medium rounded-xl text-surface-600 hover:bg-slate-50 hover:text-slate-900 transition-all duration-150';
  };

  // inline style only for active gradient pill
  const navLinkStyle = (path, exact) => {
    const active = isPathActive(path, exact);
    return active
      ? { background: 'linear-gradient(135deg, #2a2a72 0%, #1338be 100%)', boxShadow: '0 2px 12px rgba(19,56,190,0.28)' }
      : undefined;
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">

      {/* ── Brand + Workspace Switcher ── */}
      <div className="flex-shrink-0 border-b border-surface-100">
        {collapsed ? (
          <div className="flex items-center justify-center py-4">
            <img src="/logo.svg" alt="Z" className="w-8 h-8" />
          </div>
        ) : (
          <div className="px-4 py-3.5">
            <WorkspaceSwitcher
              currentWorkspaceId={localStorage.getItem('activeWorkspaceId')}
              onCreateNew={onCreateWorkspace}
            />
          </div>
        )}
      </div>

      {/* ── HiveLink — opens in its own tab ──
           An anchor, not a NavLink. HiveLink renders full-window with its own
           rail and sidebar and its own stylesheet; routing to it inside this
           layout would put two sidebars on screen. A new tab also means chat
           keeps running while you work in Zentrix, which is how people
           actually use a messenger. `noopener` is required with `_blank`. */}
      <div className="flex-shrink-0 px-3 pt-3 pb-2">
        <a
          href="/hivelink"
          target="_blank"
          rel="noopener noreferrer"
          onClick={closeMobile}
          title={collapsed ? '52Hertz — opens in a new tab' : 'Opens in a new tab'}
          className={`flex items-center gap-3 rounded-xl transition-all duration-200 relative overflow-hidden group ${
            collapsed ? 'justify-center p-3' : 'px-3.5 py-2.5'
          } hover:shadow-md hover:shadow-brand-500/20 hover:-translate-y-px`}
          style={{ background: 'linear-gradient(135deg, #2a2a72 0%, #1338be 100%)' }}
        >
          <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
            style={{ background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.08) 50%, transparent 60%)' }} />

          <span className="flex-shrink-0 relative z-10">
            <svg width={collapsed ? 22 : 20} height={collapsed ? 22 : 20} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L19.5 6.5V15.5L12 20L4.5 15.5V6.5L12 2Z" stroke="rgba(255,255,255,0.35)" strokeWidth="1.2" fill="rgba(255,255,255,0.08)" />
              <path d="M8 9.5C9 8.5 10.5 8.5 11 9.5C11.5 10.5 13 10.5 14 9.5C15 8.5 16 8.5 16.5 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" fill="none" />
              <path d="M8 12C9 11 10.5 11 11 12C11.5 13 13 13 14 12C15 11 16 11 16.5 11.5" stroke="rgba(255,255,255,0.75)" strokeWidth="1.5" strokeLinecap="round" fill="none" />
              <path d="M9.5 14.5C10.3 13.8 11.2 13.8 11.6 14.5C12 15.2 12.9 15.2 13.8 14.5C14.4 14 15.2 14 15.5 14.3" stroke="rgba(255,255,255,0.45)" strokeWidth="1.4" strokeLinecap="round" fill="none" />
              <circle cx="18.5" cy="5.5" r="2.5" fill="#34d399" />
              <circle cx="18.5" cy="5.5" r="2.5" fill="#34d399" className="animate-ping" style={{ animationDuration: '2s' }} />
            </svg>
          </span>

          {!collapsed && (
            <>
              <div className="flex-1 min-w-0 relative z-10">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-bold text-white tracking-tight">52Hertz</span>
                  <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-full bg-white/20 text-white/90 uppercase tracking-wide">Chat</span>
                </div>
                <p className="text-[10px] text-white/55 mt-px">Work at a higher frequency</p>
              </div>
              <span className="flex-shrink-0 w-2 h-2 rounded-full bg-emerald-400 relative z-10 shadow-sm" />
            </>
          )}
        </a>
      </div>

      {/* ── Navigation ── */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin px-3 py-3 space-y-1">
        {NAV_SECTIONS.map((section) => (
          <div key={section.label} className="mb-1">
            {/* Section header */}
            {!collapsed && (
              <button
                onClick={() => toggleSection(section.label)}
                aria-expanded={expandedSections[section.label] !== false}
                aria-label={`Toggle ${section.label} section`}
                className="w-full flex items-center justify-between px-3 py-1.5 mb-0.5 group"
              >
                <span className="text-2xs font-semibold text-surface-500 uppercase tracking-[0.08em]">
                  {section.label}
                </span>
                <ChevronDown
                  size={12}
                  className={`text-surface-500 transition-transform duration-200 opacity-0 group-hover:opacity-100 ${
                    expandedSections[section.label] ? '' : '-rotate-90'
                  }`}
                />
              </button>
            )}

            {/* Section items — filtered by user permissions */}
            {(collapsed || expandedSections[section.label]) &&
              section.items.filter(item => hasPermission(item.path)).map((item) => {
                const isReady = READY_PATHS.has(item.path);
                const active = isPathActive(item.path, item.exact);
                const Tag = isReady ? NavLink : 'div';
                const tagProps = isReady ? { to: item.path, onClick: closeMobile } : {};

                return (
                  // `title` is always set now: labels are `truncate`, and the
                  // narrower sidebar clips the longer ones ("Invoicing (Legacy)",
                  // "Accounts & Sales"). Previously only the collapsed state had
                  // a tooltip, so a clipped label was unreadable with no way to
                  // see the full text.
                  <Tag
                    key={item.path}
                    className={navLinkClass(item.path, item.exact)}
                    style={navLinkStyle(item.path, item.exact)}
                    title={item.label}
                    {...tagProps}
                  >
                    {/* Icon: gradient when idle, white when active, muted when disabled */}
                    <GradientIcon
                      icon={item.icon}
                      size={collapsed ? 20 : 18}
                      isActive={active}
                      isDisabled={!isReady}
                    />

                    {!collapsed && (
                      <>
                        <span className="flex-1 truncate">{item.label}</span>
                        {!isReady && (
                          <span className="text-2xs font-medium text-surface-500 bg-surface-100 px-1.5 py-0.5 rounded">
                            Soon
                          </span>
                        )}
                        {item.badge != null && (
                          <span className="text-2xs font-bold text-white bg-primary-500 px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </Tag>
                );
              })}
          </div>
        ))}
      </nav>

      {/* ── Footer: Quick Create + Collapse — UNCHANGED logic ── */}
      <div className="flex-shrink-0 border-t border-surface-100 p-3 space-y-2">
        {!window.zentrixDesktop && !collapsed && (
          <NavLink
            to="/download"
            className={({ isActive }) =>
              `w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium rounded-xl transition-colors ${
                isActive ? 'text-white shadow-sm' : 'text-surface-600 hover:text-surface-700 hover:bg-surface-50'
              }`
            }
            style={({ isActive }) =>
              isActive ? { background: 'linear-gradient(135deg, #2a2a72 0%, #1338be 100%)' } : undefined
            }
          >
            {({ isActive }) => (
              <>
                <Monitor size={15} color={isActive ? '#ffffff' : '#2a2a72'} />
                Get Desktop App
              </>
            )}
          </NavLink>
        )}
        {!window.zentrixDesktop && collapsed && (
          <NavLink
            to="/download"
            className="w-full flex items-center justify-center px-3 py-2 hover:bg-surface-100 rounded-xl transition-colors"
            title="Get Desktop App"
          >
            <Monitor size={18} color="#2a2a72" />
          </NavLink>
        )}
        {!collapsed && (
          <button
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm font-semibold text-white rounded-xl hover:opacity-90 transition-all shadow-sm"
            style={{ background: 'linear-gradient(135deg, #2a2a72 0%, #1338be 100%)' }}
            onClick={() => {/* Will open create modal */}}
          >
            <Plus size={16} />
            Quick Create
          </button>
        )}
        <button
          onClick={() => dispatch(toggleSidebarCollapse())}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-surface-600 hover:text-surface-700 hover:bg-surface-100 rounded-xl transition-colors"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed
            ? <PanelLeftOpen size={18} color="#2a2a72" />
            : <PanelLeftClose size={18} color="#2a2a72" />
          }
          {!collapsed && <span className="text-xs font-medium">Collapse</span>}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* ── Mobile overlay — UNCHANGED ── */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-surface-950/40 backdrop-blur-[3px] animate-modal-overlay"
            onClick={closeMobile}
          />
          <aside className="relative w-72 h-full bg-white shadow-modal animate-slide-right">
            <button
              onClick={closeMobile}
              aria-label="Close navigation menu"
              className="absolute top-3 right-3 p-1.5 text-surface-500 hover:text-surface-600 rounded-lg hover:bg-surface-100 z-10"
            >
              <X size={18} />
            </button>
            {sidebarContent}
          </aside>
        </div>
      )}

      {/* ── Desktop sidebar — UNCHANGED structure ── */}
      <aside
        className={`hidden lg:flex flex-col h-screen sticky top-0 bg-white border-r border-surface-200 transition-all duration-200 ease-spring flex-shrink-0 ${
          collapsed ? 'w-sidebar-collapsed' : 'w-sidebar'
        }`}
      >
        {sidebarContent}
      </aside>
    </>
  );
};

export default Sidebar;